/**
 * Type-safe route guards für TanStack-Router `beforeLoad`.
 *
 * Wir cachen das `auth.me`-Query manuell, weil tRPC@v11's createTRPCReact
 * keinen direkten `queryOptions`-Helper exportiert. Vor jedem geguarteten
 * Route-Load fetchen wir `me` via direktem fetch gegen `/api/trpc/auth.me`
 * — caching macht TanStack-Query darüber automatisch.
 */
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import type { AppRouter } from '@hirebotiq-ops/api/trpc';
import type { inferRouterOutputs } from '@trpc/server';

type Me = inferRouterOutputs<AppRouter>['auth']['me'];

async function fetchMe(): Promise<Me> {
  const res = await fetch('/api/trpc/auth.me', { credentials: 'include' });
  if (!res.ok) return null;
  const body = (await res.json()) as { result?: { data?: { json?: Me } } };
  return body.result?.data?.json ?? null;
}

export async function requireRole(
  queryClient: QueryClient,
  allow: ReadonlyArray<'ceo' | 'internal_staff' | 'worker' | 'client' | 'pending'>,
): Promise<NonNullable<Me>> {
  const me = await queryClient.fetchQuery({
    queryKey: ['auth.me'],
    queryFn: fetchMe,
    staleTime: 30_000,
  });

  if (!me) throw redirect({ to: '/' });
  if (!allow.includes(me.role)) throw redirect({ to: '/forbidden' });
  return me;
}
